import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { uploadAudio, getAudioList, scheduleAudio, deleteAudio , getAudio } from '../services/api';
import {AppConstants} from '../AppConstants';
import { Utils } from '../Utils';
import AlertBox from '../components/AlertBox';

const Audio = () => {
    const navigate = useNavigate();
    const [isRecording, setIsRecording] = useState(false);
    const [audioBlob, setAudioBlob] = useState(null);
    const [isBusy, setBusy] = useState(false);
    const [message, setAlertMessage] = useState(null);
    const [audioList, setAudioList] = useState([]);
    const [schedulingId, setSchedulingId] = useState(null);
    const [scheduleDateTime, setScheduleDateTime] = useState('');
    const [scheduleError, setScheduleError] = useState('');
    const [user, setUser] = useState(null);
    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const audioPlayerRef = useRef(null);
    

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (!userData) {
            navigate('/login');
            return;
        }
        setBusy(true);
        var userCpy = JSON.parse(userData);
        setUser(userCpy);
        fetchAudioList(userCpy.rooms.split(",")[0]);
        setBusy(false);
    }, [navigate]);
    
    const getAssignedRoomId = () =>{
        return user.rooms.split(",")[0];
    };
    const fetchAudioList = async (roomId) => {
        if(!roomId) return;
        
        try {
            setBusy(true);
            const response = await getAudioList(roomId);
            if (response.data) {
                setAudioList(response.data);
            }
        } catch (err) {
            console.error("Error fetching audio list:", err);
        } finally {
            setBusy(false);
        }
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorderRef.current = new MediaRecorder(stream, {
                mimeType: 'audio/webm'
            });
            chunksRef.current = [];

            mediaRecorderRef.current.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunksRef.current.push(e.data);
                }
            };

            mediaRecorderRef.current.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
                setAudioBlob(blob);
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorderRef.current.start();
            setIsRecording(true);
            setAlertMessage(null);
        } catch (err) {
            console.error("Error accessing microphone:", err);
            setAlertMessage('Error accessing microphone.');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
        }
    };

    const handleUpload = async () => {
        var roomId = getAssignedRoomId();
        if (!audioBlob || !roomId) return;
        
        if (audioBlob.size > 500000) {
            setAlertMessage('Recording too long (max 30 seconds).');
            return;
        }
        
        setBusy(true);
        try {
            await uploadAudio(roomId, audioBlob);
            setAlertMessage('Voice message saved!');
            setAudioBlob(null);
            fetchAudioList(getAssignedRoomId());
        } catch (err) {
            setAlertMessage('Failed to save message.');
        } finally {
            setBusy(false);
        }
    };

    const formatDateTimeForBackend = (dateTime) => {
        // Backend expects format: "YYYY-MM-DDTHH:MM" (without seconds, milliseconds, or timezone)
        const date = new Date(dateTime);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const handleSchedule = (messageId) => {
        setSchedulingId(messageId);
        setScheduleDateTime('');
        setScheduleError('');
    };

    const handleScheduleSubmit = async () => {
        var roomId = getAssignedRoomId();
        if (!scheduleDateTime || !roomId) {
            setScheduleError('Please select a date and time');
            return;
        }

        const selected = new Date(scheduleDateTime);
        const now = new Date();
        const minTime = new Date(now.getTime() + 1 * 60 * 1000);

        if (selected < minTime) {
            setScheduleError('Schedule time cannot be less than current time.');
            return;
        }
        setBusy(true);
        try {
            const formattedDateTime = formatDateTimeForBackend(scheduleDateTime);
            await scheduleAudio(schedulingId, roomId, formattedDateTime);
            setSchedulingId(null);
            setScheduleDateTime('');
            fetchAudioList(roomId);
            setAlertMessage('Audio scheduled successfully!');
        } catch (err) {
            setScheduleError(err.message || 'Failed to schedule audio');
        }finally{
            setBusy(false);
        }
    };
    const getLocalToday = () => {
        const today = new Date();
        const offset = today.getTimezoneOffset();
        const localDate = new Date(today.getTime() - (offset * 60 * 1000));
        return localDate.toISOString().split('T')[0];
    };

    const getMinTime = () => {
        const now = new Date();
        // Add 1 minute
        now.setMinutes(now.getMinutes() + 1);
        // Format as HH:mm
        return now.toTimeString().slice(0, 5);
    };

    const handleScheduleNow = async (messageId) => {
        var roomId = getAssignedRoomId();
        if (!roomId) {
            setAlertMessage('Room ID not found');
            return;
        }

        // Schedule for 1 minutes from now for immediate playback
        const now = new Date();
        const scheduleTime = new Date(now.getTime() + 1 * 60 * 1000);
        const scheduleDateTimeStr = formatDateTimeForBackend(scheduleTime);

        setBusy(true);
        try {
            await scheduleAudio(messageId, roomId, scheduleDateTimeStr);
            fetchAudioList(roomId);
            setAlertMessage('Audio scheduled to play now!');
        } catch (err) {
            setAlertMessage('Failed to schedule audio: ' + (err.message || 'Unknown error'));
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async (messageId) => {
        if (!window.confirm('Are you sure you want to delete this recording?')) {
            return;
        }
        try {
            setBusy(true);
            var roomId = getAssignedRoomId();
            await deleteAudio(messageId);
            fetchAudioList(roomId);
            setAlertMessage('Recording deleted');
        } catch (err) {
            setAlertMessage('Failed to delete recording');
        }finally{
            setBusy(false);
        }
    };

    const handlePlay = async (messageId) => {
        /*Play audio locally*/
        try {
            const response = await getAudio(messageId);
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to play audio');
            }
            
            const blob = await response.blob();
            const mt = blob.type;
            const url = URL.createObjectURL(blob);
            
            if (audioPlayerRef.current) {
                audioPlayerRef.current.src = url;
                audioPlayerRef.current.play();
            }
        } catch (err) {
            setAlertMessage('Cannot play: ' + (err.message || 'Audio not available yet'));
        }
    };


    const canDelete = (status, playOn) => {
        if(playOn === AppConstants.NULL_DATE_STR) return true;
        if (status === AppConstants.REQUEST_STATUS_INITIATED && playOn) {
            const minDt = new Date(AppConstants.NULL_DATE_STR);
            const playTime = new Date(playOn);
            const now = new Date();
            return ( 
                playTime > new Date(now.getTime() + 60 * 60 * 1000) || 
                playTime <= minDt
            );
        }
        return false;
    };

    const canPlay = (playOn) => {
        return true;
        /*
        if (!playOn) return false;
        const playTime = new Date(playOn);
        const now = new Date();
        return playTime <= now;*/
    };

    

    return (
        <div style={{ 
            width: '100%', 
            maxWidth: '480px', 
            margin: '0 auto',
            minHeight: '100vh',
            background: '#ffffff'
        }}>
            {/* Header */}
            <header style={{
                padding: '12px 16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: '#f0f0f0',
                borderBottom: '1px solid #e0e0e0'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <img 
                        src="/dog_mascot.png" 
                        alt="Logo"
                        style={{ 
                            height: '32px',
                            width: 'auto',
                            objectFit: 'contain'
                        }}
                        onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'inline';
                        }}
                    />
                    <span style={{ fontSize: '24px', display: 'none' }}>🐕</span>
                    <h2 style={{ 
                        margin: 0, 
                        fontSize: '18px', 
                        fontWeight: 'bold', 
                        color: '#000',
                        fontFamily: 'Arial, sans-serif'
                    }}>
                        My Pup : Audio
                    </h2>
                </div>
                <button 
                    onClick={() => navigate('/video-stream')} 
                    style={{ 
                        background: 'transparent', 
                        border: 'none', 
                        color: '#AE3041',
                        fontSize: '20px',
                        cursor: 'pointer',
                        padding: '4px',
                        fontFamily: 'Arial, sans-serif'
                    }}
                >
                    ⬆️
                </button>
            </header>

            <div style={{ padding: '24px', textAlign: 'center' }}>
                {/* Record Button */}
                {!audioBlob ? (
                    <div>
                        <button
                            onClick={isRecording ? stopRecording : startRecording}
                            style={{
                                width: '120px',
                                height: '120px',
                                borderRadius: '50%',
                                background: '#f5f5f5',
                                border: isRecording ? '5px solid green' : '5px solid white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '48px',
                                cursor: 'pointer',
                                margin: '0 auto 16px'
                            }}
                        >
                            🎤
                        </button>
                        <p style={{ 
                            color: '#000', 
                            fontSize: '14px', 
                            marginBottom: '32px',
                            fontFamily: 'Arial, sans-serif'
                        }}>
                            Press to record new message. Max 30 sec long
                        </p>
                    </div>
                ) : (
                    <div style={{ marginBottom: '24px' }}>
                        <p style={{ color: '#000', marginBottom: '16px' }}>Recording ready</p>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            <button
                                onClick={handleUpload}
                                disabled={isBusy}
                                style={{
                                    padding: '8px 16px',
                                    background: '#AE3041',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                {isBusy ? 'Saving...' : 'Save'}
                            </button>
                            <button
                                onClick={() => setAudioBlob(null)}
                                style={{
                                    padding: '8px 16px',
                                    background: '#f5f5f5',
                                    color: '#000',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                Discard
                            </button>
                        </div>
                    </div>
                )}
                {/*
                {message && (
                    <div style={{
                        padding: '8px',
                        background: message.includes('Failed') || message.includes('Cannot') ? 'rgba(207, 102, 121, 0.1)' : 'rgba(3, 218, 198, 0.1)',
                        color: message.includes('Failed') || message.includes('Cannot') ? '#cf6679' : '#03dac6',
                        borderRadius: '4px',
                        marginBottom: '16px',
                        fontSize: '14px'
                    }}>
                        {message}
                    </div>
                )}*/}

                {/* All Recordings */}
                <h3 style={{ 
                    textAlign: 'left', 
                    fontWeight: 'bold', 
                    marginBottom: '16px',
                    color: '#000',
                    fontFamily: 'Arial, sans-serif',
                    fontSize: '16px'
                }}>
                    All recordings
                </h3>
                
                {isBusy ? (
                    <div>Working. Please wait...</div>
                ) : audioList.length === 0 ? (
                    <div style={{ color: '#666', padding: '32px' }}>No recordings yet</div>
                ) : (
                    <div style={{ textAlign: 'left', height:'300px',overflowX:'hidden',overflowY:'scroll' }}>
                        {audioList.map((audio) => (
                            <div key={audio.MessageId} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '12px 0',
                                borderBottom: '1px solid #e0e0e0'
                            }}>
                                {Utils.getStatusIcon(audio.Status, audio.PlayOn) && (
                                    <span style={{ fontSize: '20px', minWidth: '24px' }}>
                                        {Utils.getStatusIcon(audio.Status, audio.PlayOn)}
                                    </span>
                                )}
                                <div style={{ 
                                    flex: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <div style={{ 
                                        width: '40px', 
                                        height: '20px',
                                        background: '#e0e0e0',
                                        borderRadius: '2px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-around',
                                        padding: '0 4px'
                                    }}>
                                        <div style={{ width: '2px', height: '12px', background: '#666' }}></div>
                                        <div style={{ width: '2px', height: '16px', background: '#666' }}></div>
                                        <div style={{ width: '2px', height: '10px', background: '#666' }}></div>
                                        <div style={{ width: '2px', height: '14px', background: '#666' }}></div>
                                    </div>
                                    <span style={{ color: '#666', fontSize: '14px' }}>
                                        On {Utils.formatDateTime(audio.PlayOn)}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    {audio.Status === AppConstants.REQUEST_STATUS_INITIATED && (
                                        <>
                                            <button
                                                onClick={() => handleSchedule(audio.MessageId)}
                                                style={{
                                                    padding: '4px 8px',
                                                    background: 'transparent',
                                                    border: 'none',
                                                    color: '#AE3041',
                                                    cursor: 'pointer',
                                                    fontSize: '14px',
                                                    textDecoration: 'underline'
                                                }}
                                            >
                                                Schedule
                                            </button>
                                            <button
                                                onClick={() => handleScheduleNow(audio.MessageId)}
                                                style={{
                                                    padding: '4px 8px',
                                                    background: 'transparent',
                                                    border: 'none',
                                                    color: '#AE3041',
                                                    cursor: 'pointer',
                                                    fontSize: '14px',
                                                    textDecoration: 'underline'
                                                }}
                                            >
                                                Schedule Now
                                            </button>
                                        </>
                                    )}
                                    {canPlay(audio.PlayOn) && (
                                        <button
                                            onClick={() => handlePlay(audio.MessageId)}
                                            style={{
                                                width: '32px',
                                                height: '32px',
                                                background: '#f5f5f5',
                                                border: '1px solid #e0e0e0',
                                                borderRadius: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                color: '#AE3041'
                                            }}
                                        >
                                            ▶
                                        </button>
                                    )}
                                    {canDelete(audio.Status, audio.PlayOn) && (
                                        <button
                                            onClick={() => handleDelete(audio.MessageId)}
                                            style={{
                                                width: '32px',
                                                height: '32px',
                                                background: '#f5f5f5',
                                                border: '1px solid #e0e0e0',
                                                borderRadius: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                color: '#AE3041'
                                            }}
                                        >
                                            🗑️
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Schedule Modal */}
                {schedulingId && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}>
                        <div style={{
                            background: '#ffffff',
                            padding: '24px',
                            borderRadius: '8px',
                            width: '90%',
                            maxWidth: '400px'
                        }}>
                            <h3 style={{ marginBottom: '16px', color: '#000', fontWeight: 'bold' }}>
                                Schedule audio message play
                            </h3>
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                                <input
                                    type="date"
                                    value={scheduleDateTime ? scheduleDateTime.split('T')[0] : ''}
                                    onChange={(e) => {
                                        const date = e.target.value;
                                        const time = scheduleDateTime ? scheduleDateTime.split('T')[1] : '12:00';
                                        setScheduleDateTime(`${date}T${time}`);
                                    }}
                                    min={getLocalToday()}
                                    style={{
                                        flex: 1,
                                        padding: '8px',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '4px'
                                    }}
                                />
                                <input
                                    type="time"
                                    value={scheduleDateTime ? scheduleDateTime.split('T')[1] : ''}
                                    onChange={(e) => {
                                        const date = scheduleDateTime ? scheduleDateTime.split('T')[0] : new Date().toISOString().split('T')[0];
                                        setScheduleDateTime(`${date}T${e.target.value}`);
                                    }}
                                    style={{
                                        flex: 1,
                                        padding: '8px',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '4px'
                                    }}
                                    min={getMinTime()}
                                />
                                {/*<select
                                    style={{
                                        padding: '8px',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '4px'
                                    }}
                                >
                                    <option>am</option>
                                    <option>pm</option>
                                </select>*/}
                            </div>
                            {scheduleError && (
                                <div style={{ color: '#cf6679', fontSize: '14px', marginBottom: '16px' }}>
                                    {scheduleError}
                                </div>
                            )}
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                    onClick={() => {
                                        setSchedulingId(null);
                                        setScheduleDateTime('');
                                        setScheduleError('');
                                    }}
                                    style={{
                                        flex: 1,
                                        padding: '8px',
                                        background: '#f5f5f5',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleScheduleSubmit}
                                    style={{
                                        flex: 1,
                                        padding: '8px',
                                        background: '#AE3041',
                                        color: '#ffffff',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    OK
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {/** modals */}
                <AlertBox
                    show = {message != null}
                    title="Alert"
                    message={message}
                    onOK= {()=>setAlertMessage(null)}/>
                <audio ref={audioPlayerRef} style={{ display: 'none' }} />
            </div>
        </div>
    );
};

export default Audio;

